<template>
  <div
    class="d-flex align-center justify-start dppl-simple-checkbox"
    :class="`dppl-simple-checkbox-${size}`"
  >
    <input
      :id="inputId"
      v-model="checkboxModel"
      type="checkbox"
      :disabled="disabled"
      :class="{ 'checkbox-disabled': disabled }"
      :style="{ borderRadius }"
      :aria-label="option?.label || 'Checkbox'"
      @click.self="() => {}"
    >
    <label
      v-if="!hideLabel"
      :for="inputId"
      class="ml-3"
    >
      <slot
        name="option-label"
        :option="option"
      >{{ option.label }}</slot>
    </label>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const emits = defineEmits(['update:modelValue']);

const props = defineProps({
  option: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  borderRadius: {
    type: String,
    default: '3px',
  },
});

const inputId = computed(() => `input-${props.option?.label || ''}-${props.option?.id || ''}`);

const checkboxModel = computed({
  get: () => props.modelValue,
  set: (value) => {
    emits('update:modelValue', value);
  },
});
</script>

<style lang="less" scoped>
.dppl-simple-checkbox {
  input[type=checkbox] {
    position: relative;
    flex-shrink: 0;
    box-sizing: border-box;
    border: 1px solid @grey-900;
    appearance: none;
    outline: 1px solid transparent;
    cursor: pointer;

    &:hover {
      border-color: @text-brand;
    }

    &::before {
      position: absolute;
      content: '';
      display: block;
      border-style: solid;
      border-color: @white;
      transform: rotate(45deg);
      opacity: 0;
      border-radius: 1px;
    }

    &:focus-visible {
      outline: revert;
    }
  }
}

.dppl-simple-checkbox-sm input[type=checkbox] {
  width: 12px;
  height: 12px;

  &::before {
    top: 1.5px;
    left: 4px;
    width: 4px;
    height: 8px;
    border-width: 0 2px 2px 0;
    border-radius: 0.5px;
  }
}

.dppl-simple-checkbox-md input[type=checkbox] {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;

  &::before {
    top: 3px;
    left: 6px;
    width: 5px;
    height: 10px;
    border-width: 0 2px 2px 0;
    border-radius: 1px;
  }
}

.dppl-simple-checkbox-lg input[type=checkbox] {
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;

  &::before {
    top: 4px;
    left: 8px;
    width: 7px;
    height: 12px;
    border-width: 0 2px 2px 0;
    border-radius: 1px;
  }
}

.dppl-simple-checkbox input[type=checkbox]:checked {
  color: @white;
  border-color: @text-brand;
  background: @bg-brand-default;

  &::before {
    opacity: 1;
  }

  ~ label::before {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
}

.dppl-simple-checkbox input[type=checkbox].checkbox-disabled,
.dppl-simple-checkbox input[type=checkbox]:disabled {
  cursor: default;
  border-color: @grey-400;
  background-color: @bg-dark;

  &:hover {
    border-color: @grey-400;
  }
}

label {
  padding: 5px 0px;
}
</style>
